import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'epn-ui/umd/dist/components/Button';
import Loader from 'epn-ui/umd/dist/components/Loader';

import { GetStaticPaths, GetStaticProps } from 'next';
import Grid from 'epn-ui/umd/dist/components/Grid';
import useI18n, { getLocalePaths } from '@i18n';
import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import {
    IGetCreativesDataFormatted,
    IGetOffersDataFormatted,
    IGetPlacementsDataFormatted,
    IGetUserProfileDataFormatted,
} from '@api/types';

import Tabs, { TabKeyEnum } from '@components/pages/creatives/Tabs';
import Filters from '@pages/creatives/Filters';
import Search from '@pages/creatives/Search';
import CreativeCard from '@components/pages/creatives/CreativeCard';
import ModalEridToken from '@components/pages/creatives/ModalEridToken';
import ModalAddPlacement from '@components/pages/creatives/ModalAddPlacement';
import ModalSelectPlacement from '@components/pages/creatives/ModalSelectPlacement';

// HOCs
import WithRole, { rolesEnum } from '@HOCs/WithRole';

import BaseSemanticLink from '@base/BaseSemanticLink';

import routes from '@config/routes.json';
import {
    ALL_OFFERS,
    DEFAULT_FILTER_VALUE,
    GOT_BY_DATA,
    MODAL_TYPES,
} from '@components/pages/creatives/config';

import {
    IFilters,
    IOffers,
    IPreparedCreativesData,
    PlacementByOfferIdStatus,
} from '@components/pages/creatives/creatives.interface';
import useLocalStorage from '@hooks/useLocalStorage';
import { CustomGetStaticPathsResult, CustomGetStaticPropsResult } from '../../../../types';

const {
    useCreativeOffers,
    useOffers,
    useCreatives,
    useCreativesBySearch,
    useUserProfile,
    usePlacements,
} = apiHooks;

const CREATIVE_FIELDS =
    'id,link,offerId,code,description,type,hash,sizes,erid,extInfoType,placement';

const MIN_SEARCH_VALUE_LENGTH = 3;

const Creatives: React.FC = () => {
    const { t, locale } = useI18n('Creatives');
    const { t: tTools } = useI18n('Tools');
    const { t: tCommon } = useI18n('Common');
    const [isActiveTab, setIsActiveTab] = useState<boolean>(true);
    const [isMarkingTab, setIsMarkingTab] = useState<boolean>(false);
    // нужен для отображения лодера сразу после нажатия на "в архив"/"вернуть из архива"
    // тк список креативов обновляется после выполнения запроса на смену статуса креатива
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    // Собираю id офферов, для которых нужно проверить площадки.
    const [needStatusPlacement, setNeedStatusPlacement] = useState<string[] | null>(null);
    const [modalType, setModalType] = useState<keyof typeof MODAL_TYPES | null>(null);
    const [modalData, setModalData] = useState<{
        creative?: IGetCreativesDataFormatted;
        offer?: IGetOffersDataFormatted;
    } | null>(null);

    const [filter, setFilter] = useState<IFilters>(DEFAULT_FILTER_VALUE);
    const [checkedReduction, setCheckedReduction] = useLocalStorage('isCheckedReduction', false);
    const [activeReduction, setActiveReduction] = useLocalStorage('activeReduction', GOT_BY_DATA);

    const { data: userData } = useUserProfile<IGetUserProfileDataFormatted>();
    const hasSearchValue: boolean = filter.inputSearch.length >= MIN_SEARCH_VALUE_LENGTH;

    const creativesParams = {
        name: filter.search,
        isDeleted: !isActiveTab,
        offersIds: filter.offerId,
        fields: CREATIVE_FIELDS,
        limit: 20,
        search: hasSearchValue ? filter.inputSearch : null,
    };

    const creativesOnlyMarking = {
        ...creativesParams,
        madeInMarking: isMarkingTab,
    };

    const creativesDependencies: (string | boolean)[] = [
        isActiveTab,
        isMarkingTab,
        JSON.stringify(filter),
    ];

    const useCreativeHook = hasSearchValue ? useCreativesBySearch : useCreatives;

    const {
        data: creativesData = [],
        nextPage,
        isReachingEnd,
        mutate: refreshCreatives,
        isLoading: creativeDataIsLoading,
        error,
    } = useCreativeHook({
        params: isMarkingTab ? creativesOnlyMarking : creativesParams,
        dependencies: creativesDependencies,
    }) as ICreateUseRequestInfiniteResponse<IGetCreativesDataFormatted[]>;

    const preprareOfferIds = useCallback((): void => {
        const ids: string[] = creativesData.reduce(
            (acc, creative: IGetCreativesDataFormatted): string[] => {
                if (
                    !creative.extInfoType?.placementId.length &&
                    !creative.erid?.length &&
                    creative.extInfoType?.refProgram === '0' &&
                    !acc.includes(creative.offerId)
                ) {
                    return [...acc, creative.offerId];
                }
                return acc;
            },
            [],
        );
        setNeedStatusPlacement(ids);
    }, [creativesData]);

    useEffect((): void => {
        // Собираю offer id креативов без placement_id
        if (!userData?.hasOrdData) return;
        if (creativesData.length > 0) {
            preprareOfferIds();
        }
    }, [creativesData, preprareOfferIds, userData?.hasOrdData]);

    // Получаю список площадок, для креативов, у которых нет placement_id
    const { data: otherPlacements = [] } = usePlacements({
        condition: needStatusPlacement?.length > 0,
        params: {
            offerIds: needStatusPlacement?.join(','),
            fields: 'id,status,offer_id,url',
            statuses: 'allow,waiting,review',
            limit: creativesParams.limit,
            offset: 0,
        },
        dependencies: [needStatusPlacement],
    }) as ICreateUseRequestInfiniteResponse<IGetPlacementsDataFormatted[]>;

    const { data: creativeOffersData = [] } = useCreativeOffers({
        params: { isDeleted: !isActiveTab },
        dependencies: [isActiveTab],
    }) as ICreateUseRequestInfiniteResponse<string[]>;

    const { data: offersData = [], isLoading: isOffersLoading } = useOffers({
        params: {
            lang: locale,
            viewRules: 'role_user',
            ids: creativeOffersData.join(','),
            fields: 'hasContracts,name,image,title,link_match,status',
            limit: 1000,
        },
        dependencies: [creativeOffersData, locale],
        condition: !!creativeOffersData.length,
    }) as ICreateUseRequestInfiniteResponse<IGetOffersDataFormatted[]>;

    const filtersOnChange = (name: string, value: string): void => {
        if (filter[name] !== value) {
            setFilter({ ...filter, [name]: value });
        }
    };

    const toggleTab = useCallback((key: TabKeyEnum): void => {
        const isArchiveTabActive: boolean = key === TabKeyEnum.ARCHIVE;
        const isMarkingTabActive: boolean = key === TabKeyEnum.MARKING;
        setIsActiveTab(!isArchiveTabActive);
        setIsMarkingTab(isMarkingTabActive);
        setFilter(DEFAULT_FILTER_VALUE);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const offersSelectOptions: IOffers[] = [
        ALL_OFFERS(tCommon),
        ...offersData.map(
            ({ id, name }: { id: number; name: string }): IOffers => ({
                value: id,
                label: name,
            }),
        ),
    ];

    const getOffer = useCallback(
        (offerId: string): IGetOffersDataFormatted => {
            return offersData.find(({ id }: { id: number }): boolean => +id === +offerId);
        },
        [offersData],
    );

    const getPlacementStatusByOfferId = useCallback(
        (offerId: string): PlacementByOfferIdStatus => {
            const placements: string[] = [];
            otherPlacements.forEach((placement: IGetPlacementsDataFormatted): void => {
                if (placement.offerId === offerId) {
                    placements.push(placement.status);
                }
            });
            return placements.find(p =>
                ['allow', 'waiting', 'review'].includes(p),
            ) as PlacementByOfferIdStatus;
        },
        [otherPlacements],
    );

    const handleModal = (type: keyof typeof MODAL_TYPES, data: unknown): void => {
        setModalData(data);
        setModalType(type);
    };

    const handleModalFinish = (data?: {
        creative?: IGetCreativesDataFormatted;
        offer?: IGetOffersDataFormatted;
    }): void => {
        // Если юзер выбрал площадку, отобразить модалку с токеном.
        if (modalType === MODAL_TYPES.selectPlacement) {
            setModalData(data);
            setModalType(MODAL_TYPES.getEridToken);
            return;
        }
        refreshCreatives();
        setModalData(null);
        setModalType(null);
    };

    const onModalClose = (): void => {
        setModalData(null);
        setModalType(null);
    };

    // Подготавливаю данные для креативов
    const preparedCardsData: IPreparedCreativesData[] = useMemo(
        () =>
            creativesData.reduce(
                (
                    acc: IPreparedCreativesData[],
                    creative: IGetCreativesDataFormatted,
                ): IPreparedCreativesData[] => {
                    const offer: IGetOffersDataFormatted = getOffer(creative.offerId);
                    const placementUrl = creative?.placement?.url;
                    // Проверяю условия для отображения кнопки "получить токен"
                    if (
                        !creative.extInfoType?.placementId.length &&
                        !creative.erid?.length &&
                        creative.offerId &&
                        offer?.status === 'active'
                    ) {
                        // Добавляю статус площадки для оффера креатива
                        const placementStatus: PlacementByOfferIdStatus = getPlacementStatusByOfferId(
                            creative.offerId,
                        );
                        return [
                            ...acc,
                            {
                                creativeSrc: creative,
                                placementByOfferIdStatus: placementStatus,
                                offer,
                                placementUrl,
                            },
                        ];
                    }
                    return [
                        ...acc,
                        {
                            creativeSrc: creative,
                            offer,
                            placementUrl,
                        },
                    ];
                },
                [],
            ),
        [creativesData, getOffer, getPlacementStatusByOfferId],
    );

    const defaultSearch = !Object.values(filter).some(item => item);

    const requestId: string = error?.response?.headers?.get('request-id');
    const filteredCreativesData: IPreparedCreativesData[] = useMemo((): IPreparedCreativesData[] => {
        if (isMarkingTab) {
            return preparedCardsData.filter(
                (creative: IPreparedCreativesData) =>
                    creative.creativeSrc.extInfoType.madeInMarking,
            );
        }

        return preparedCardsData;
    }, [preparedCardsData, isMarkingTab]);

    return (
        <>
            <div className="flex flex-col justify-between flex-grow lg:flex-row">
                <div className="flex flex-col xs:flex-row xs:items-center">
                    <h3 className="font-medium text-sub text-blueGray-600 xs:text-h2">
                        {tTools('My Creatives')}
                    </h3>
                </div>
                <div className="mt-[16px] sm:mt-[24px] sm:flex lg:mt-0">
                    <Search onChange={filtersOnChange} />
                    <Filters
                        offers={offersSelectOptions}
                        offerId={filter.offerId}
                        onChange={filtersOnChange}
                        className="mt-6 lg:mt-0 sm:mt-0"
                    />
                </div>
            </div>
            <div className="mb-8 md:mt-[24px] md:flex-row border-blueGray-200 mt-[16px]">
                <Tabs isActive={isActiveTab} toggleTab={toggleTab} />
                {!(isActiveTab || isMarkingTab) && (
                    <p className="pt-5 text-green-500 text-p2">
                        {t('Your archived creatives continue to work')}
                    </p>
                )}
            </div>
            {!!filteredCreativesData.length && ( // список не отображается, если загружается первая страница
                <Grid
                    gutter={[
                        { xs: 8, sm: 16, md: 32, lg: 32, xl: 32 },
                        { xs: 16, sm: 16, md: 32, lg: 32, xl: 32 },
                    ]}
                >
                    {filteredCreativesData.map(card => (
                        <Grid.Col
                            key={card.creativeSrc.id}
                            span={24}
                            xs={24}
                            sm={24}
                            md={24}
                            lg={24}
                            xl={12}
                        >
                            <CreativeCard
                                creative={card.creativeSrc}
                                offer={card.offer}
                                isArchive={!(isActiveTab || isMarkingTab)}
                                placementUrl={card.placementUrl}
                                refreshCreatives={refreshCreatives}
                                setIsDeleting={setIsDeleting}
                                isOffersLoading={isOffersLoading}
                                checkedReduction={checkedReduction}
                                setCheckedReduction={setCheckedReduction}
                                activeReduction={activeReduction}
                                setActiveReduction={setActiveReduction}
                                hasOrdData={userData?.hasOrdData}
                                placementByOfferIdStatus={card.placementByOfferIdStatus}
                                handleModal={handleModal}
                            />
                        </Grid.Col>
                    ))}
                </Grid>
            )}
            {!creativeDataIsLoading && !creativesData.length && (
                <div className="text-center text-blueGray-600">
                    {!isActiveTab ? (
                        t('Archive is empty')
                    ) : (
                        <>
                            {defaultSearch ? (
                                <>
                                    <span>
                                        {t('You dont have a Smart-banner DeepLink Affiliate Link')}{' '}
                                    </span>
                                    <BaseSemanticLink
                                        to={routes.tools.affiliateLinks}
                                        className="font-medium"
                                    >
                                        {t('Create')}{' '}
                                    </BaseSemanticLink>
                                    <span>{t('One now')}</span>
                                </>
                            ) : (
                                tCommon('List is empty')
                            )}
                        </>
                    )}
                </div>
            )}
            {(creativeDataIsLoading || isDeleting) && (
                <div className="flex justify-center w-full mt-[32px]">
                    <Loader />
                </div>
            )}
            <div className="flex mx-auto w-max mt-[32px] mb-36">
                {!isReachingEnd && !creativeDataIsLoading && (
                    <Button type="primary" onClick={() => nextPage()}>
                        {tCommon('Load more')}
                    </Button>
                )}
            </div>

            {error && (
                <div className="p-20 text-center text-p1">
                    {tCommon('Failed to load data')}{' '}
                    {requestId && <p className="font-medium">request-id: {requestId}</p>}
                </div>
            )}

            {modalType === MODAL_TYPES.getEridToken && (
                <ModalEridToken
                    onClose={handleModalFinish}
                    creative={modalData.creative}
                    offer={modalData.offer}
                />
            )}
            {modalType === MODAL_TYPES.addPlacement && (
                <ModalAddPlacement
                    onClose={onModalClose}
                    handleModalFinish={handleModalFinish}
                    offer={modalData.offer}
                />
            )}
            {modalType === MODAL_TYPES.selectPlacement && (
                <ModalSelectPlacement
                    onClose={onModalClose}
                    handleModalFinish={handleModalFinish}
                    creative={modalData.creative}
                    offer={modalData.offer}
                />
            )}
        </>
    );
};

export const getStaticProps: GetStaticProps = async ({
    params,
}): Promise<CustomGetStaticPropsResult> => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async (): Promise<CustomGetStaticPathsResult> => {
    return { paths: getLocalePaths(), fallback: false };
};

export default WithRole(Creatives, [rolesEnum.user]);
